import { ActionMeta } from 'react-select';
import { FormComponentValue } from 'components/common/form/layout/control';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material';
import { getBlendColors } from 'hooks/useDesign';
import { Params as UrlParams } from '@remix-run/router/dist/utils';
import React from 'react';

export type ReactSelectProps = {
    component?: string;
    uid?: string;
    id?: string;
    name?: string;
    addonLabel?: string;
    disabled?: boolean;
    data: Array<FormComponentValue>;
    value: any;
    update: (value: any, fullValue: any, actionMeta: ActionMeta<FormComponentValue>) => void;
    settingsUrl?: string;
    placeholder?: string;
    isMulti?: boolean;
    isCheckbox?: boolean;
    creatable?: boolean;
    isLoading?: boolean;
    onMenuOpen?: () => void;
    onMenuClose?: () => void;
    optionComponent?: React.Component;
    onInputChange?: (nextValue: string) => void;
    customPortalTarget?: any;
    className?: string;
    isClearable?: boolean;
    isMultiOneRowMode?: boolean;
    tooltip?: string;
    async?: string;
    urlParams?: UrlParams;
    applyVirtualList?: boolean;
    getOptionLabel?: (option: any) => any;
    filterOption?: (option: any, inputValue: string) => boolean;
    noOptionsMessage?: (obj: { inputValue: string }) => React.ReactNode;
    closeMenuOnSelect?: boolean;
    overrideComponents?: any;
    dataTest?: string;
    classNames?: any;
    keepValueOrder?: boolean;
};

export const useReactSelectStyles = () => {
    const appTheme = useTheme();

    const reactSelectTheme = (defaultTheme: any) => {
        return {
            ...defaultTheme,
            borderRadius: 3,
            colors: {
                ...defaultTheme.colors,
                primary: appTheme.palette.primary.main,
                primary25: alpha(appTheme.palette.primary.main, 0.25),
                primary50: alpha(appTheme.palette.primary.main, 0.5),
                primary75: alpha(appTheme.palette.primary.main, 0.75),
            },
            spacing: {
                ...defaultTheme.spacing,
                controlHeight: 32,
                menuGutter: 5,
            },
        };
    };

    const checkIsApplyMultiOneRowMode = (state: any) => {
        return state.selectProps.isMulti && state.selectProps.isMultiOneRowMode && !state.selectProps.menuIsOpen;
    };
    const checkIsApplyActiveMultiOneRowMode = (state: any) => {
        return state.selectProps.isMulti && state.selectProps.isMultiOneRowMode && state.selectProps.menuIsOpen;
    };

    // ToDo [Stas]: get and pass theme variables
    // Maybe need to:
    // - replace Dropdown icon
    // - set styles for different of default state
    // - consider search field to replace
    const reactSelectStyles = {
        container: (provided: any, state: any) => {
            const applyActiveMultiOneRowMode = checkIsApplyActiveMultiOneRowMode(state);

            const activeMultiOneRowModeStyles = applyActiveMultiOneRowMode
                ? {
                      position: 'absolute',
                      inset: '0 0 auto 0',
                      zIndex: 3,
                  }
                : {};
            return {
                ...provided,
                fontSize: `${appTheme.typography.fontSize}px`, // appTheme
                width: '100%',
                ...activeMultiOneRowModeStyles,
            };
        },
        control: (provided: any, state: any) => {
            let borderColor = getBlendColors(appTheme, alpha(appTheme.palette.text.primary, 0.4));
            let borderColorSecondary = getBlendColors(appTheme, alpha(appTheme.palette.text.primary, 0.04));
            if (state.isFocused) borderColor = borderColorSecondary = alpha(appTheme.palette.primary.main, 0.8);
            if (state.menuIsOpen) borderColor = borderColorSecondary = appTheme.palette.primary.main;

            const dropDownIconColor =
                state.isFocused || state.menuIsOpen ? appTheme.palette.primary.main : appTheme.palette.text.primary;
            return {
                ...provided,
                borderColor: state.isDisabled
                    ? getBlendColors(appTheme, alpha(appTheme.palette.text.primary, 0.08))
                    : borderColor,
                boxShadow: state.menuIsOpen ? `0 0 0 2px ${alpha(appTheme.palette.primary.main, 0.24)}` : 'none',
                backgroundColor: state.isDisabled
                    ? getBlendColors(appTheme, alpha(appTheme.palette.text.primary, 0.08))
                    : appTheme.palette.background.default,
                minHeight: appTheme.size.defaultHeight + 'px',
                '.custom-select__dropdown-icon': {
                    color: state.isDisabled
                        ? alpha(appTheme.palette.text.primary, 0.24)
                        : appTheme.palette.primary.main,
                },

                '.design-variant--secondary &': {
                    borderRadius: '4px',
                    borderColor: borderColorSecondary,
                    backgroundColor:
                        state.isFocused || state.menuIsOpen
                            ? appTheme.palette.background.default
                            : getBlendColors(appTheme, alpha(appTheme.palette.text.primary, 0.04)),
                    '.custom-select__dropdown-icon': {
                        color: state.isDisabled ? alpha(appTheme.palette.text.primary, 0.24) : dropDownIconColor,
                    },
                    '&:hover': {
                        backgroundColor:
                            state.isFocused || state.menuIsOpen
                                ? appTheme.palette.background.default
                                : getBlendColors(appTheme, alpha(appTheme.palette.text.primary, 0.08)),
                    },
                },
                '&:hover': {
                    borderColor: state.menuIsOpen
                        ? appTheme.palette.primary.main
                        : alpha(appTheme.palette.primary.main, 0.8),
                },
            };
        },
        placeholder: (provided: any, state: any) => {
            return {
                ...provided,
                color: alpha(appTheme.palette.text.primary, 0.64),
                whiteSpace: 'nowrap', //hotfix for multi placeholder
                '.design-variant--secondary &': {
                    color: state.isDisabled
                        ? alpha(appTheme.palette.text.primary, 0.24)
                        : alpha(appTheme.palette.text.primary, 0.64),
                },
            };
        },
        input: (provided: any, state: any) => {
            return {
                ...provided,
                color: appTheme.palette.text.primary,
            };
        },
        singleValue: (provided: any, state: any) => {
            return {
                ...provided,
                color: state.isDisabled ? alpha(appTheme.palette.text.primary, 0.64) : appTheme.palette.primary.main,
                '.design-variant--secondary &': {
                    color: state.isDisabled
                        ? alpha(appTheme.palette.text.primary, 0.24)
                        : appTheme.palette.text.primary,
                },
            };
        },
        multiValue: (provided: any, state: any) => {
            const applyMultiOneRowMode = checkIsApplyMultiOneRowMode(state);
            const multiOneRowModeStyles = applyMultiOneRowMode
                ? {
                      flexShrink: 0,
                  }
                : {};
            return {
                ...provided,
                backgroundColor: state.isDisabled
                    ? alpha(appTheme.palette.text.primary, 0.08)
                    : alpha(appTheme.palette.primary.main, 0.12),
                color: appTheme.palette.primary.main,
                margin: '1px',
                ...multiOneRowModeStyles,
            };
        },
        multiValueLabel: (provided: any, state: any) => {
            const applyMultiOneRowMode = checkIsApplyMultiOneRowMode(state);
            return {
                ...provided,
                color: state.isDisabled ? alpha(appTheme.palette.text.primary, 0.64) : appTheme.palette.primary.main,
                fontSize: `${appTheme.typography.fontSize}px`,
                paddingRight: applyMultiOneRowMode || state.isDisabled ? '6px' : '2px',

                '> svg': {
                    marginRight: 8,
                },

                '> img': {
                    top: 1,
                    marginRight: 5,
                    position: 'relative',
                },
            };
        },
        multiValueRemove: (provided: any) => {
            return {
                ...provided,
                cursor: 'pointer',
            };
        },
        dropdownIndicator: (provided: any, state: any) => {
            return {
                ...provided,
                padding: '0 4px',
                color: appTheme.palette.primary.main,
            };
        },
        clearIndicator: (provided: any, state: any) => {
            return {
                ...provided,
                padding: '0',
                marginRight: '4px',
                cursor: 'pointer',
                color: appTheme.palette.text.primary,
            };
        },
        valueContainer: (provided: any, state: any) => {
            const applyMultiOneRowMode = checkIsApplyMultiOneRowMode(state);
            const multiOneRowModeStyles = applyMultiOneRowMode
                ? {
                      flexWrap: 'nowrap',
                      '&:after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          bottom: 0,
                          width: '25px',
                          backgroundImage: state.isDisabled
                              ? `linear-gradient(90deg, ${alpha(
                                    getBlendColors(appTheme, alpha(appTheme.palette.text.primary, 0.08)),
                                    0,
                                )} 0%, ${getBlendColors(appTheme, alpha(appTheme.palette.text.primary, 0.08))} 100%)`
                              : `linear-gradient(90deg, ${alpha(
                                    appTheme.palette.background.default,
                                    0,
                                )} 0%, ${appTheme.palette.background.default} 100%)`,
                      },
                  }
                : {};
            return {
                ...provided,
                padding: '0 0 0 11px',
                '&.custom-select__value-container--is-multi.custom-select__value-container--has-value': {
                    paddingLeft: 0,
                    paddingTop: 0,
                    paddingBottom: 0,
                },
                '.design-variant--secondary &': {
                    padding: '0 6px',
                },
                ...multiOneRowModeStyles,
            };
        },
        menu: (provided: any, state: any) => {
            return {
                ...provided,
                background: appTheme.componentSettings.dropdown.backgroundColor,
                borderRadius: appTheme.componentSettings.dropdown.borderRadius + 'px',
                boxShadow: '0px 4px 16px 0 rgba(0, 0, 0, 0.16)',
                color: appTheme.palette.text.primary,
                zIndex: 3, //overlap grid .rs-table-header-row-wrapper = 2
                overflow: 'hidden', //clip selected border with big radius
                padding: '4px 0',
                '& .react-virtualized-list': {
                    //Experimental for virtual list auto menu size
                    height: 'auto !important',
                },
                '& .react-virtualized-list__item > .custom-select__option': {
                    minHeight: '100%',
                },
                '&.custom-select__menu--stretch-to-200': {
                    width: 'auto',
                    minWidth: '100%',
                    maxWidth: '200px',
                },
                '&.custom-select__menu--width-275': {
                    width: '275px',
                },
                '&.custom-select__menu--indent': {
                    '&.placement--bottom': {
                        marginBottom: '15px',
                    },
                    '&.placement--top': {
                        marginTop: '15px',
                    },
                },
            };
        },
        menuList: (provided: any, state: any) => {
            return {
                ...provided,
                paddingTop: 0,
                paddingBottom: 0,
            };
        },
        option: (provided: any, state: any) => {
            const selectHighlight = state.isSelected && state?.selectProps?.isCheckbox !== true;
            const separator = state?.value === 'sep';
            let background = appTheme.componentSettings.dropdown.backgroundColor;
            const opacityLevel = (100 - appTheme.componentSettings.dropdown.hoverOpacity) / 100;
            if (state.isFocused && !state.isDisabled) background = alpha(appTheme.palette.primary.main, opacityLevel);
            if (selectHighlight) background = alpha(appTheme.palette.primary.main, opacityLevel + 0.04);
            if (state.isDisabled) background = alpha(appTheme.componentSettings.dropdown.textColor, 0.04);

            let color = state.isDisabled
                ? alpha(appTheme.componentSettings.dropdown.textColor, 0.64)
                : appTheme.componentSettings.dropdown.textColor;
            if (selectHighlight) color = appTheme.palette.primary.main;

            if (separator && state) {
                state.data.label = '';
            }

            return {
                ...provided,
                cursor: 'pointer',
                padding: separator ? '0' : '7px 12px',
                backgroundColor: background,
                color: color,
                position: 'relative',
                fontWeight: state.isDisabled ? '600' : undefined,
                wordWrap: 'break-word',

                '&:active': {
                    backgroundColor: background,
                },

                '&:before': {
                    content: '""',
                    position: 'absolute',
                    inset: '0 auto 0 0',
                    width: separator ? '100%' : '0',
                    height: separator ? '1px' : '',
                    background: separator ? alpha(appTheme.palette.text.primary, 0.16) : '',
                    top: separator ? '50%' : '',
                    borderLeft: '1px solid',
                    borderColor: selectHighlight ? appTheme.palette.primary.main : 'transparent',
                },

                '> svg': {
                    marginRight: 8,
                    opacity: 0.6,
                },

                '> img': {
                    top: 1,
                    marginRight: 5,
                    position: 'relative',
                },
            };
        },
        indicatorSeparator: () => ({ display: 'none' }),
        menuPortal: (provided: any) => {
            return {
                ...provided,
                zIndex: appTheme.zIndex.tabsPanel - 1,
                '.content-editor-aside-panel-active &': {
                    //todo: review and refactor to default aside modal
                    zIndex: appTheme.zIndex.tabsPanel + 5,
                },
                '.data-preview-panel-active &': {
                    zIndex: appTheme.zIndex.tabsPanel + 1,
                },
                // declared last so it wins the cascade tie when a popup is open on top of an aside/data-preview panel
                '.popup-component ~ &': {
                    zIndex: appTheme.zIndex.modal + 50,
                },
            };
        },
    };

    return {
        theme: reactSelectTheme,
        styles: reactSelectStyles,
    };
};
